import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, Play, Camera, Shield, FlaskConical, Clock, Hash } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuickRound() {
  const [roundNumber, setRoundNumber] = useState(1);
  const [headsUp, setHeadsUp] = useState(60);
  const [duration, setDuration] = useState(1);
  const [roundType, setRoundType] = useState('test');
  const [league, setLeague] = useState('racing');
  const [activeRound, setActiveRound] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    checkActiveRound();
    const interval = setInterval(checkActiveRound, 5000);
    return () => clearInterval(interval);
  }, []);

  const checkActiveRound = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/round-status`);
      if (res.data.status !== 'idle') {
        setActiveRound(res.data);
      } else {
        setActiveRound(null);
      }
    } catch (error) {
      console.error('Failed to check round status:', error);
    }
  };

  const triggerRound = async () => {
    if (!window.confirm(`${roundType === 'test' ? 'TEST' : 'OFFICIAL'} Round ${roundNumber} — proceed?`)) return;
    setTriggering(true);
    try {
      const params = new URLSearchParams({
        round_number: roundNumber,
        heads_up_seconds: headsUp,
        duration_seconds: Math.round(duration * 3600),
        round_type: roundType
      });
      await axios.post(`${BACKEND_URL}/api/admin/start-round?${params}`, {}, { withCredentials: true });
      toast.success(`Round ${roundNumber} (${roundType}) triggered!`);
      checkActiveRound();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger round');
    } finally {
      setTriggering(false);
    }
  };

  const captureRound = async () => {
    if (!window.confirm(`Capture Round ${roundNumber} as ${roundType.toUpperCase()}?${roundType === 'test' ? '\n\nTest points will be auto-removed after 30 minutes.' : ''}`)) return;
    setCapturing(true);
    try {
      if (roundType === 'test') {
        // For test rounds, capture BOTH leagues automatically
        for (const lg of ['racing', 'moto']) {
          const params = new URLSearchParams({
            round_number: roundNumber,
            round_name: `Round ${roundNumber} (Test)`,
            league: lg,
            round_type: roundType
          });
          await axios.post(`${BACKEND_URL}/api/admin/capture-round?${params}`, {}, { withCredentials: true });
        }
        toast.success(`Test Round ${roundNumber} captured for both Racing & Moto! Points will auto-clear in 30 min.`);
      } else {
        const params = new URLSearchParams({
          round_number: roundNumber,
          round_name: `Round ${roundNumber}`,
          league: league,
          round_type: roundType
        });
        await axios.post(`${BACKEND_URL}/api/admin/capture-round?${params}`, {}, { withCredentials: true });
        toast.success(`Round ${roundNumber} captured for ${league}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to capture round');
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="quick-round">
      <div className="bg-card border border-zinc-800 p-6">
        <div className="mb-6">
          <h2 className="font-heading font-bold text-2xl flex items-center">
            <Zap className="mr-2 text-yellow-500" size={24} />
            Quick Round
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Instantly trigger and capture rounds for emergencies or testing
          </p>
        </div>

        {/* Active Round Banner */}
        {activeRound && (
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-700 flex items-center gap-3" data-testid="active-round-banner">
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
            <div>
              <p className="text-yellow-400 font-bold">Round {activeRound.round_number} is currently {activeRound.status}</p>
              <p className="text-yellow-300/60 text-sm">
                {activeRound.round_type === 'test' ? 'TEST ROUND' : 'OFFICIAL ROUND'}
                {' — '}Wait for it to complete or end it before starting a new one.
              </p>
            </div>
          </div>
        )}

        {/* Round Type Toggle */}
        <div className="mb-6">
          <label className="block text-xs text-zinc-500 mb-2 font-mono uppercase">Round Type</label>
          <div className="flex gap-0">
            <button
              onClick={() => setRoundType('official')}
              data-testid="quick-round-type-official"
              className={`flex items-center gap-2 px-6 py-3 font-bold transition-colors ${
                roundType === 'official'
                  ? 'bg-green-700 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              <Shield size={18} />
              Official
            </button>
            <button
              onClick={() => setRoundType('test')}
              data-testid="quick-round-type-test"
              className={`flex items-center gap-2 px-6 py-3 font-bold transition-colors ${
                roundType === 'test'
                  ? 'bg-orange-600 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              <FlaskConical size={18} />
              Test
            </button>
          </div>
          <p className={`text-xs mt-2 ${roundType === 'test' ? 'text-orange-400' : 'text-green-400'}`}>
            {roundType === 'test'
              ? 'Test round points will be automatically removed from standings after 30 minutes.'
              : 'Official round points are permanent and count towards the championship.'}
          </p>
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">
              <Hash size={12} className="inline mr-1" />ROUND NUMBER
            </label>
            <input
              type="number"
              value={roundNumber}
              onChange={(e) => setRoundNumber(parseInt(e.target.value) || 1)}
              className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-lg font-mono"
              min="1"
              data-testid="quick-round-number"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">
              <Clock size={12} className="inline mr-1" />HEADS-UP (sec)
            </label>
            <input
              type="number"
              value={headsUp}
              onChange={(e) => setHeadsUp(parseInt(e.target.value) || 0)}
              className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white font-mono"
              min="0"
              data-testid="quick-round-headsup"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">
              <Clock size={12} className="inline mr-1" />DURATION (hours)
            </label>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(parseFloat(e.target.value) || 0)}
              className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white font-mono"
              min="0"
              step="0.5"
              data-testid="quick-round-duration"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">LEAGUE</label>
            <select
              value={league}
              onChange={(e) => setLeague(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white"
              data-testid="quick-round-league"
            >
              <option value="racing">Superturismo</option>
              <option value="moto">MotoMSF</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={triggerRound}
            disabled={triggering || !!activeRound}
            className={`flex-1 flex items-center justify-center gap-2 py-3 font-bold text-lg transition-colors disabled:opacity-40 ${
              roundType === 'test'
                ? 'bg-orange-600 hover:bg-orange-500 text-white'
                : 'bg-green-700 hover:bg-green-600 text-white'
            }`}
            data-testid="quick-round-trigger-btn"
          >
            <Play size={20} />
            {triggering ? 'Triggering...' : `Trigger ${roundType === 'test' ? 'Test' : 'Official'} Round ${roundNumber}`}
          </button>
          <button
            onClick={captureRound}
            disabled={capturing}
            className="flex items-center justify-center gap-2 px-8 py-3 bg-purple-700 hover:bg-purple-600 text-white font-bold text-lg transition-colors disabled:opacity-40"
            data-testid="quick-round-capture-btn"
          >
            <Camera size={20} />
            {capturing ? 'Capturing...' : 'Capture'}
          </button>
        </div>

        {/* Info Box */}
        <div className={`mt-6 p-4 border text-sm ${
          roundType === 'test'
            ? 'bg-orange-950/20 border-orange-900/50 text-orange-300'
            : 'bg-green-950/20 border-green-900/50 text-green-300'
        }`}>
          <p className="font-bold mb-1">
            {roundType === 'test' ? 'Test Round Info:' : 'Official Round Info:'}
          </p>
          {roundType === 'test' ? (
            <ul className="space-y-1 list-disc list-inside">
              <li>Points are temporarily added to standings for 30 minutes</li>
              <li>After 30 minutes, the test snapshot is auto-deleted and leaderboard recalculated</li>
              <li>Test rounds do not affect boost freezing for official rounds</li>
            </ul>
          ) : (
            <ul className="space-y-1 list-disc list-inside">
              <li>Points are permanently added to standings</li>
              <li>Boost effects are frozen at capture time</li>
              <li>Only removable by admin manual intervention</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
